import React, { useRef, useState } from "react";
import { StyleConfigModel } from "@/pages/Messaging/types";
import { IconTrash } from "@velaro/velaro-component-lib";

interface Props {
  setStageLogoFile: (file: File | null) => void;
  update: (updates: Partial<StyleConfigModel>) => void;
  workingStyleConfig: Partial<StyleConfigModel>;
  field: keyof Pick<StyleConfigModel, "logoUrl" | "queueGraphicUrl" | "headerBackgroundImageUrl" | "launcherIconUrl" | "contentWidgetImageUrl">;
  label?: string;
  accept?: string;
  maxSizeMb?: number;
  maxWidth?: number;
  maxHeight?: number;
  sizeGuidance?: string;
  previewClassName?: string;
  /** Height of the preview box in pixels (default 72). Always full width. */
  previewBoxHeight?: number;
  /** Background color for the preview box — pass your theme color so white logos remain visible */
  previewBackground?: string;
}

/**
 * Resize an image to fit within maxWidth × maxHeight using a canvas,
 * preserving aspect ratio. Returns a data URL (PNG).
 */
function resizeToFit(src: string, maxW: number, maxH: number): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const ratio = Math.min(maxW / img.naturalWidth, maxH / img.naturalHeight, 1);
      const w = Math.round(img.naturalWidth * ratio);
      const h = Math.round(img.naturalHeight * ratio);
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      canvas.getContext("2d")!.drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL("image/png"));
    };
    img.src = src;
  });
}

export default function LogoUpload(props: Props) {
  const {
    accept = "image/png,image/svg+xml",
    maxSizeMb = 5,
    maxWidth,
    maxHeight,
    sizeGuidance,
    previewClassName,
    previewBoxHeight = 72,
    previewBackground,
  } = props;

  const fileInput = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const defaultGuidance = maxWidth && maxHeight
    ? `Up to ${maxSizeMb}MB • Supports PNG, SVG • Recommended: ${maxWidth}×${maxHeight}px (larger images auto-resized)`
    : `Up to ${maxSizeMb}MB • Supports PNG, SVG`;

  const handleFile = async (file: File) => {
    setError(null);

    if (file.size > maxSizeMb * 1024 * 1024) {
      setError(`File exceeds ${maxSizeMb}MB limit. Please choose a smaller image.`);
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      let dataUrl = e.target?.result?.toString();
      if (!dataUrl) return;

      if (maxWidth || maxHeight) {
        const img = new Image();
        img.onload = async () => {
          const needsResize =
            (maxWidth && img.naturalWidth > maxWidth) ||
            (maxHeight && img.naturalHeight > maxHeight);

          if (needsResize) {
            dataUrl = await resizeToFit(dataUrl!, maxWidth ?? 9999, maxHeight ?? 9999);
          }

          props.update({ [props.field]: dataUrl });
          props.setStageLogoFile(file);
          setFileName(file.name);
        };
        img.src = dataUrl;
      } else {
        props.update({ [props.field]: dataUrl });
        props.setStageLogoFile(file);
        setFileName(file.name);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleBrowse = () => {
    fileInput.current?.click();
  };

  const handleRemove = () => {
    props.update({ [props.field]: "" });
    props.setStageLogoFile(null);
    setFileName(null);
    setError(null);
  };

  const url = props.workingStyleConfig[props.field];

  return (
    <div>
      <div className="text-sm font-medium mb-2">{props.label}</div>

      {url ? (
        <div className="border rounded-md p-3 bg-slate-50 mb-2">
          {/* Full-width preview — consistent height across all upload types */}
          <div
            className="w-full rounded overflow-hidden border border-slate-200 flex items-center justify-center mb-2"
            style={{ height: previewBoxHeight, backgroundColor: previewBackground || "#f8fafc" }}
          >
            <img
              src={url}
              alt={`${props.label} preview`}
              className={previewClassName ?? "max-w-full max-h-full object-contain"}
            />
          </div>
          <div className="flex items-center justify-between">
            <span className="truncate text-sm text-slate-500 min-w-0 mr-2">
              {fileName ?? (props.label ? `${props.label} uploaded` : "Uploaded")}
            </span>
            <button
              onClick={handleRemove}
              className="shrink-0 text-slate-400 hover:text-red-600 transition flex items-center gap-1 text-xs"
              aria-label="Remove file"
            >
              <IconTrash className="h-3.5 w-3.5" />
              Remove
            </button>
          </div>
        </div>
      ) : (
        <label
          onClick={handleBrowse}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`w-full flex flex-col items-center justify-center border-2 border-dashed rounded-md px-4 py-5 text-sm text-slate-500 cursor-pointer transition gap-1 ${
            dragOver ? "bg-blue-50 border-primary" : "bg-slate-50 hover:bg-slate-100"
          }`}
        >
          <span className="text-center leading-snug">
            Drop a file here, or{" "}
            <span className="underline text-primary">browse</span>
          </span>
        </label>
      )}

      <input
        type="file"
        accept={accept}
        ref={fileInput}
        className="hidden"
        onChange={(e) => {
          const file = e.currentTarget.files?.[0];
          if (file) handleFile(file);
          e.currentTarget.value = "";
        }}
      />

      {error ? (
        <div className="mt-2 text-xs font-medium text-red-600">{error}</div>
      ) : (
        <div className="mt-2 text-xs font-normal text-slate-500">
          {sizeGuidance ?? defaultGuidance}
        </div>
      )}
    </div>
  );
}
